import _ from 'lodash';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Img } from 'react-image';
import { Checkbox } from '@/core/Checkbox.atom';
import { IconWithSpinner } from '@/core/IconWithSpinner.atom';
import { useKey } from '@/core/hooks/useKey.hook';
import { isSystemTest } from '@/core/utilities';
import { headlessRenderMode } from '@/services/headlessCapture.utilities';
import { TRACK } from '@/track/track.constants';
import { doTrack } from '@/track/track.service';
import { Button, Carousel, Modal } from '@seeqdev/qomponents';

interface TourModalIF {
  displayTour: boolean;
  tourTitle: string;
  tourSteps: { image: string; heading: string; imageHeight?: number }[];
  onClose: () => void; // called only if the "Don't Show Again" checkbox is checked when closing
  testId: string;
  trackName: string;
}

export const TourModal = ({ displayTour, tourTitle, tourSteps, onClose, trackName, testId }: TourModalIF) => {
  const { t } = useTranslation();

  const [showTour, setShowTour] = useState(displayTour);
  const [dontShowAgain, setDontShowAgain] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);

  const rightArrowKey = useKey(39);
  const leftArrowKey = useKey(37);

  useEffect(() => {
    if (rightArrowKey && activeIndex !== tourSteps.length - 1) {
      setActiveIndex(activeIndex + 1);
    }
  }, [rightArrowKey]);

  useEffect(() => {
    if (leftArrowKey && activeIndex !== 0) {
      setActiveIndex(activeIndex - 1);
    }
  }, [leftArrowKey]);

  const closeTour = () => {
    setShowTour(false);

    const trackInfo = JSON.stringify({
      gifNumber: activeIndex + 1,
      totalGifs: tourSteps.length,
      dontShowChecked: dontShowAgain,
    });

    doTrack(TRACK.TRAINING, `close ${trackName} tour modal`, trackInfo);

    if (dontShowAgain) {
      onClose();
    }
  };

  const loadingPlaceholder = (showSpinner: boolean) => (
    <div className="flexFill text-center lightGreyBorder flexRowContainer tw:h-sq-425!">
      {showSpinner && (
        <div className="flexFill flexRowContainer tw:items-center tw:justify-center">
          <IconWithSpinner spinning={true} extraClassNames="fa-4x" />
        </div>
      )}
    </div>
  );

  const trackSlideNavigation = (eventKey: number, direction: string) =>
    doTrack(TRACK.TRAINING, `${direction} Arrow clicked`, `Slide ${eventKey + 1}`);

  const onSlide = (eventKey: number, direction: string) => {
    setActiveIndex(eventKey);
    trackSlideNavigation(eventKey, direction);
  };

  return showTour && !headlessRenderMode() && !isSystemTest() ? (
    <Modal
      open={true}
      onClose={closeTour}
      data-testid={testId}
      size="4xl"
      title={t(tourTitle)}
      submitButtonLabel={t('TOUR.GOT_IT')}
      hideSubmitButton={true}
      modalFooter={
        <div className="flexColumnContainer tw:items-center tw:justify-end">
          <Checkbox
            id="dontShowAgain"
            classes="mr10"
            label="TOUR.DONT_SHOW_AGAIN"
            isChecked={dontShowAgain}
            onChange={() => setDontShowAgain(!dontShowAgain)}
          />
          <Button label={t('TOUR.GOT_IT')} testId="gotItButton" variant="theme" onClick={closeTour} />
        </div>
      }>
      <div className="carouselContainer">
        <Carousel
          activeIndex={activeIndex}
          continuous={false}
          onSlide={onSlide}
          iconExtraClassNames="backgroundColorWhite caruselArrowButton"
          showArrows
          carouselItems={_.map(tourSteps, (step, index) => (
            <div key={index} className="flexRowContainer flexFill">
              <h4 className="tw:mt-0!">{t(step.heading)}</h4>
              <Img
                src={step.image}
                height={step.imageHeight ?? 425}
                width="100%"
                loader={loadingPlaceholder(true)}
                unloader={loadingPlaceholder(false)}
              />
            </div>
          ))}
        />
      </div>
    </Modal>
  ) : null;
};
